import { useCallback, useEffect, useState } from "react";
import { subDays } from "date-fns";

/**
 * Iter184 (B04 UX fix) — one account-level date range for the whole app.
 *
 * Previously every page picked its own default (Deals=today, Leaderboard=30d,
 * Commissions=90d, Analytics=30d), which is why the same customer's numbers
 * couldn't be reconciled by eye across pages. This hook stores a single
 * `{from, to}` in localStorage, seeded to the last 30 days on first use,
 * and every page that opts in reads/writes to the same key.
 *
 * Notes:
 * - Dates are serialised as ISO strings so JSON.stringify survives a reload.
 * - We treat corrupt/legacy values defensively — a bad payload falls back
 *   to the 30-day default without throwing.
 */

const STORAGE_KEY = "warmap_account_date_range";

function defaultRange() {
  return { from: subDays(new Date(), 30), to: new Date() };
}

function serialise(range) {
  if (!range?.from) return null;
  return JSON.stringify({
    from: range.from.toISOString(),
    to: (range.to || range.from).toISOString(),
  });
}

function deserialise(raw) {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed?.from || !parsed?.to) return null;
    const from = new Date(parsed.from);
    const to = new Date(parsed.to);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
    return { from, to };
  } catch {
    return null;
  }
}

export function useAccountDateRange() {
  const [range, setRange] = useState(() => {
    if (typeof window === "undefined") return defaultRange();
    return deserialise(window.localStorage.getItem(STORAGE_KEY)) || defaultRange();
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const s = serialise(range);
    if (s) window.localStorage.setItem(STORAGE_KEY, s);
    // Broadcast so other tabs / other hook instances mounted on other
    // routes update in lock-step. `storage` events don't fire in the
    // same tab, so we also dispatch a custom event.
    window.dispatchEvent(new CustomEvent("warmap:date-range-changed", { detail: range }));
  }, [range]);

  // Listen for changes from other hook instances on the same tab.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onChange = (e) => {
      const next = e.detail;
      if (!next?.from) return;
      // Avoid a state-set loop by comparing serialised form.
      if (serialise(next) !== serialise(range)) {
        setRange({ from: new Date(next.from), to: new Date(next.to) });
      }
    };
    window.addEventListener("warmap:date-range-changed", onChange);
    return () => window.removeEventListener("warmap:date-range-changed", onChange);
  }, [range]);

  const setSafe = useCallback((next) => {
    if (!next?.from) return;
    setRange({
      from: next.from instanceof Date ? next.from : new Date(next.from),
      to: (next.to || next.from) instanceof Date ? (next.to || next.from) : new Date(next.to || next.from),
    });
  }, []);

  return [range, setSafe];
}
